'use client';

import { useId } from 'react';
import type { ChangeEventHandler } from 'react';
import { cn } from 'utils/styles';
import { HintDisclosure } from './HintDisclosure';
import type { CalculatorField } from './types';

const LABEL = 'block text-body-saans';

const SELECT =
  'mt-3 h-16 w-full rounded-lg border border-content-secondary bg-bg-primary px-6 text-body-saans text-content-primary outline-none transition-shadow hover:ring-[6px] hover:ring-bg-tertiary focus:border-content-primary focus:ring-[6px] focus:ring-bg-tertiary';

/** The same alert treatment `CalculatorInput` gives an invalid field, so the two read as one form. */
const SELECT_INVALID =
  'border-util-alert-500 bg-util-alert-100 hover:ring-util-alert-200 focus:border-util-alert-500 focus:ring-util-alert-200';

export const SELECT_PLACEHOLDER = 'Select';

interface SelectFieldProps<Key extends string> {
  field: CalculatorField;
  options: readonly { key: Key; label: string }[];
  /** Undefined for a required choice with no default, which shows the disabled placeholder until one is made. */
  value?: Key;
  onChange: (key: Key) => void;
  error?: string;
}

export const SelectField = <Key extends string>({ field, options, value, onChange, error }: SelectFieldProps<Key>) => {
  const selectId = useId();
  const hintId = `${selectId}-hint`;
  const errorId = `${selectId}-error`;
  const describedBy =
    [error ? errorId : undefined, field.hint ? hintId : undefined].filter(id => id !== undefined).join(' ') ||
    undefined;
  const handleChange: ChangeEventHandler<HTMLSelectElement> = event => {
    const selected = options.find(option => option.key === event.target.value);

    if (selected) onChange(selected.key);
  };

  const labelElement = (
    <label htmlFor={selectId} className={LABEL}>
      {field.label}
    </label>
  );

  return (
    <div className="w-full">
      {field.hint ? (
        <div className="flex items-center gap-2">
          {labelElement}
          <HintDisclosure hintId={hintId} label={field.label} hint={field.hint} />
        </div>
      ) : (
        labelElement
      )}
      <select
        id={selectId}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        value={value ?? ''}
        onChange={handleChange}
        className={cn(SELECT, value === undefined && 'text-content-secondary', error && SELECT_INVALID)}>
        {value === undefined && (
          <option value="" disabled>
            {SELECT_PLACEHOLDER}
          </option>
        )}
        {options.map(option => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p id={errorId} role="alert" aria-live="assertive" className="mt-3 text-body-saans text-util-alert-500">
          {error}
        </p>
      )}
    </div>
  );
};
